// ─────────────────────────────────────────────────────────────────────────────
// V2PublicInvoice — /invoice/:publicId, the page clients open from WhatsApp links.
// Same data sources as v1 (public, no login):
//   GET /api/invoices/public/:publicId, GET /api/auth/public/seller/:publicId (seller), GET /api/bank/public/invoice/:publicId
// The document is the shared InvoicePaper (identical to the in-app invoice view).
// Print / Save as PDF uses the browser (as v1) — the server PDF needs a login.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import "./v2.css";
import "./v2-pages.css";
import API_BASE from "../apiConfig";
import usePageTitle from "../hooks/usePageTitle";
import { useAppearance } from "./appearance";
import { Glyph } from "./icons";
import { Skel, useMedia } from "./primitives";
import { InvoicePaper, computeInvoice } from "./V2InvoiceView";

const money = (v) => `₹${(Math.round((Number(v) || 0) * 100) / 100).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—");

export default function V2PublicInvoice() {
  const { publicId } = useParams();
  useAppearance();
  const isPhone = useMedia("(max-width: 760px)");
  const [inv, setInv] = useState(null);
  const [seller, setSeller] = useState(null);
  const [bank, setBank] = useState(null);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState("");
  usePageTitle(inv ? `Invoice ${inv.invoiceNumber}` : "Invoice");

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/invoices/public/${publicId}`);
        const d = await r.json().catch(() => ({}));
        if (!r.ok || !d?._id) throw new Error(d.error || "This invoice link isn't valid.");
        setInv(d);
        const [u, b] = await Promise.all([
          fetch(`${API_BASE}/api/auth/public/seller/${publicId}`).then((x) => (x.ok ? x.json() : null)).catch(() => null),
          fetch(`${API_BASE}/api/bank/public/invoice/${publicId}`).then((x) => (x.ok ? x.json() : null)).catch(() => null),
        ]);
        setSeller(u || {}); setBank(b);
      } catch (e) { setErr(e.message || "This invoice link isn't valid."); }
    })();
  }, [publicId]);

  const calc = useMemo(() => (inv ? computeInvoice(inv) : null), [inv]);
  const print = () => {
    document.documentElement.setAttribute("data-v2-print", "invoice");
    const done = () => { document.documentElement.removeAttribute("data-v2-print"); window.removeEventListener("afterprint", done); };
    window.addEventListener("afterprint", done);
    window.print();
  };
  const copy = async (label, text) => { try { await navigator.clipboard.writeText(text); setCopied(label); setTimeout(() => setCopied(""), 1600); } catch { /* ignore */ } };

  if (err) return (
    <div className="v2 v2pi"><div className="v2-backdrop" aria-hidden="true" />
      <div className="v2pi-missing"><span className="v2-boot-mark">e</span><h1>Invoice not found</h1><p>{err} Please ask the sender for a new link.</p></div>
    </div>);

  const active = inv?.status === "ACTIVE";
  const due = calc?.due || 0;
  const paid = inv?.paymentStatus === "PAID" || (calc && due <= 0);
  const overdue = calc?.overdueDays > 0 && active && !paid;
  const upi = bank?.upiId && active && due > 0 ? `upi://pay?pa=${bank.upiId}&pn=${encodeURIComponent(seller?.companyName || "")}&am=${due.toFixed(2)}&cu=INR&tn=${encodeURIComponent(inv.invoiceNumber)}` : null;
  const name = seller?.companyName || "";

  return (
    <div className="v2 v2pi">
      <div className="v2-backdrop" aria-hidden="true" />
      <header className="v2pi-top">
        <div className="v2pi-from">
          <span className="v2iv-mark">{(name || "?").trim().slice(0, 1).toUpperCase()}</span>
          <div><span className="v2pi-label">Invoice from</span><strong>{name || (inv ? "—" : "Loading…")}</strong></div>
        </div>
        <button type="button" className="v2-btn" onClick={print} disabled={!inv}><Glyph name="download" size={16} /> {isPhone ? "Save" : "Print or save PDF"}</button>
      </header>

      <main className="v2pi-main">
        {!inv || !calc || seller === null ? <><Skel h={150} r={26} /><Skel h={760} r={20} /></> : <>
          <section className={`v2pi-summary${paid ? " is-paid" : overdue ? " is-overdue" : ""}`}>
            <div className="v2pi-amount">
              <span className="v2pi-label">{!active ? "Cancelled invoice" : paid ? "Paid in full" : "Amount due"}</span>
              <strong>{money(active && !paid ? due : inv.grandTotal)}</strong>
              <span className="v2pi-meta">
                {!active ? "This invoice was cancelled by the sender." : paid ? `Invoice ${inv.invoiceNumber} · thank you!` : <>{overdue ? <b>{calc.overdueDays} days overdue</b> : <>Due {fmtDate(calc.dueDate)}</>} · Invoice {inv.invoiceNumber}{calc.paid > 0 ? ` · ${money(calc.paid)} paid so far` : ""}</>}
              </span>
            </div>
            {active && !paid && (bank?.accountNumber || upi) && (
              <div className="v2pi-pay">
                {upi && isPhone && <a className="v2-btn v2-btn--primary v2pi-upi" href={upi}><Glyph name="rupee" size={16} /> Pay {money(due)} with UPI</a>}
                {bank?.upiId && <button type="button" className="v2pi-copy" onClick={() => copy("upi", bank.upiId)}><span>UPI ID</span><b>{bank.upiId}</b><em>{copied === "upi" ? "Copied" : "Copy"}</em></button>}
                {bank?.accountNumber && <button type="button" className="v2pi-copy" onClick={() => copy("acc", bank.accountNumber)}><span>A/c {bank.bankName ? `· ${bank.bankName}` : ""}</span><b className="v2iv-mono">{bank.accountNumber}</b><em>{copied === "acc" ? "Copied" : "Copy"}</em></button>}
                {bank?.ifscCode && <button type="button" className="v2pi-copy" onClick={() => copy("ifsc", bank.ifscCode)}><span>IFSC</span><b className="v2iv-mono">{bank.ifscCode}</b><em>{copied === "ifsc" ? "Copied" : "Copy"}</em></button>}
              </div>)}
          </section>
          <InvoicePaper inv={inv} calc={calc} seller={seller} bank={bank} isPhone={isPhone} />
        </>}
      </main>
      <footer className="v2pi-foot">Sent with <b>EazyBilling</b> · GST invoicing for Indian businesses</footer>
    </div>
  );
}
